export interface BudgetRow {
  id: string;
  number?: string;
  name: string;
  description: string;
  amounts: { [month: string]: number };
  isSum?: boolean;
  isGroupTotal?: boolean;
  groupKey?: string;
}

export interface BudgetRowProps {
  row: BudgetRow;
  months: string[];
  changedFields: Set<string>;
  onAmountChange: (rowId: string, month: string, value: string) => void;
  isGroupCollapsed: { [key: string]: boolean };
  onToggleGroup: (groupKey: string) => void;
  onDeleteRow: (rowId: string) => void;
  onClearRow: (rowId: string) => void;
}

export interface ApiProjection {
  year: number;
  month: number;
  history: number | null;
  target: number | null;
}

export interface ApiAccount {
  number: string | null;
  description: string | null;
  months: ApiProjection[] | null;
}

export interface ApiBudget {
  id: string;
  clientId: string;
  connectionIds: string[] | null;
  name: string | null;
  yearFrom: number;
  monthFrom: number;
  yearTo: number;
  monthTo: number;
  projectionPercentage: number;
  accounts: ApiAccount[] | null;
}

export const transformApiAccountToBudgetRow = (account: ApiAccount): BudgetRow => {
  const amounts: { [key: string]: number } = {};
  
  if (account.months) {
    account.months.forEach(month => {
      const monthKey = `${month.year}-${String(month.month).padStart(2, '0')}`;
      amounts[monthKey] = month.target || 0;
    });
  }

  return {
    id: account.number || crypto.randomUUID(),
    number: account.number || undefined,
    name: account.description || '',
    description: account.description || '',
    amounts,
    groupKey: getGroupKeyFromAccount(account.number),
  };
};

const groupDescriptions = {
  '3': 'Nettoomsättning',
  '4': 'Råvaror och förnödenheter',
  '5,6': 'Övriga externa kostnader',
  '7': 'Personalkostnader',
  '8': 'Finansiella poster',
}

const getGroupKeyFromAccount = (number: string): string | undefined => {
  if (!number) return undefined;
  const first = number[0];
  return first === '5' || first === '6' ? '5,6' : first;
}

export const groupAccountsWithSubtotals = (accounts: BudgetRow[]): BudgetRow[] => {
  const groups: { [key: string]: BudgetRow[] } = {};
  
  // Sort accounts by number
  const sortedAccounts = [...accounts].sort((a, b) => 
    (a.number || '').localeCompare(b.number || '')
  );

  // Group accounts by first digit
  sortedAccounts.forEach(account => {
    const groupKey = getGroupKeyFromAccount(account.number);
    if (groupKey) {
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(account);
    }
  });

  // Create final array with group headers
  const result: BudgetRow[] = [];
  Object.entries(groups).sort().forEach(([groupKey, groupAccounts]) => {
    // Calculate totals for the group
    const groupTotals: { [month: string]: number } = {};
    groupAccounts.forEach(account => {
      Object.entries(account.amounts).forEach(([month, amount]) => {
        groupTotals[month] = (groupTotals[month] || 0) + amount;
      });
    });

    // Add group header with totals
    result.push({
      id: `group-${groupKey}`,
      name: `Grupp ${groupKey}`,
      description: groupDescriptions[groupKey] || '-',
      amounts: groupTotals,
      isGroupTotal: true,
      groupKey,
    });

    // Add accounts
    result.push(...groupAccounts);
  });

  return result;
};